import React, { useState } from 'react';
import StepWrapper from './StepWrapper';
import { StepSection, StepChip, StepOptionCard, StepPillToggle } from './StepUI';
import { COLORS } from '../../../styles/theme';

// ids must match HOME_SQFT_BASE_PRICES' keys exactly -- this used to split the
// top end into '3000_4000' and '4000_plus', neither of which existed in the
// backend's price table (only 'over_3000'), so both silently fell back to the
// 1,500-2,000 sq ft tier -- the two largest, most expensive homes quoted like
// an average-size one. Collapsed into the one tier the backend actually prices.
const SQFT_OPTIONS = [
  { id: 'under_1000', label: 'Under 1,000 sq ft', sub: 'Small home / condo' },
  { id: '1000_1500', label: '1,000 – 1,500 sq ft', sub: 'Modest home' },
  { id: '1500_2000', label: '1,500 – 2,000 sq ft', sub: 'Average home' },
  { id: '2000_2500', label: '2,000 – 2,500 sq ft', sub: 'Larger home' },
  { id: '2500_3000', label: '2,500 – 3,000 sq ft', sub: 'Spacious home' },
  { id: 'over_3000', label: 'Over 3,000 sq ft', sub: 'Large home / estate' },
];

// Australia pilot (Nov 2026) -- AU real estate is fully metric (property
// size is always advertised in sqm, unlike the UK where sqft is still
// common in estate-agent listings), so "1,500 - 2,000 sq ft" would read as
// genuinely unfamiliar to an AU visitor, not just a minor unit mismatch.
// Display-only: same ids, same HOME_SQFT_BASE_PRICES tiers/pricing behind
// them, just the label text an AU company's visitors see (1 sqft = 0.0929
// sqm, rounded to whole sqm).
const SQFT_OPTIONS_AU_LABELS = {
  under_1000: 'Under 93 sqm',
  '1000_1500': '93 – 139 sqm',
  '1500_2000': '139 – 186 sqm',
  '2000_2500': '186 – 232 sqm',
  '2500_3000': '232 – 279 sqm',
  over_3000: 'Over 279 sqm',
};

// ids must match CLEANING_TYPE_MULTIPLIERS' keys in backend/src/config/defaults.js
// exactly -- this used to say 'deep_clean' here while the backend only ever had
// a 'deep' key, so every Deep Clean quote silently fell through to the Standard
// multiplier (1.0x instead of the correct 1.68-1.85x).
const CLEANING_TYPES = [
  { id: 'standard', label: 'Standard Clean', desc: 'Dusting, vacuuming, mopping, bathrooms, kitchen' },
  { id: 'deep', label: 'Deep Clean', desc: 'Everything in standard + appliances, baseboards, detailed scrubbing' },
  { id: 'move_in_out', label: 'Move-In / Move-Out', desc: 'Full top-to-bottom clean for vacant home' },
];

const FREQUENCY = [
  { id: 'one_time', label: 'One-Time' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'biweekly', label: 'Bi-Weekly' },
  { id: 'triweekly', label: 'Every 3 Weeks' },
  { id: 'monthly', label: 'Monthly' },
];

// ids must match HOME_EXTRAS' keys in backend/src/config/defaults.js exactly --
// 'windows_interior' and 'patio' used to not match the backend's
// 'interior_windows'/'balcony_patio' keys at all, so those two add-ons
// silently cost $0 when selected (the addon loop just skips an unknown key
// rather than erroring). 'inside_cabinets' and 'pet_hair' had no backend
// price at all until now -- added to HOME_EXTRAS alongside this fix.
// 'laundry' used to be a flat toggle here (same price whether it was 1
// bed's linens or 5) -- removed in favor of the dedicated bed-count
// control below, which prices "laundry for 4 beds" the way it's actually
// requested in practice, not as an all-or-nothing whole-home add-on.
const EXTRAS = [
  { id: 'inside_fridge', label: 'Inside Fridge' },
  { id: 'inside_oven', label: 'Inside Oven' },
  { id: 'inside_cabinets', label: 'Inside Cabinets' },
  { id: 'interior_windows', label: 'Interior Windows' },
  { id: 'garage', label: 'Garage' },
  { id: 'balcony_patio', label: 'Patio / Deck' },
  { id: 'pet_hair', label: 'Pet Hair Removal' },
  { id: 'basement', label: 'Finished Basement' },
];

export default function HomeStep({ value, onBack, onNext, primaryColor, companyConfig, country, isFinalStep, loading }) {
  // Only reflects a real, company-set discount (Services tab) -- off by
  // default, so a fresh account shows no "% off" tag on any frequency
  // until the company actually configures one.
  const frequencyDiscounts = companyConfig?.services?.homeResidential?.frequencyDiscounts || {};
  const [sqftTier, setSqftTier] = useState(value.sqftTier || '1500_2000');
  const [bedrooms, setBedrooms] = useState(value.bedrooms || '3');
  const [bathrooms, setBathrooms] = useState(value.bathrooms || '2');
  const [cleaningType, setCleaningType] = useState(value.cleaningType || 'standard');
  const [frequency, setFrequency] = useState(value.frequency || 'one_time');
  const [extras, setExtras] = useState(value.extras || []);
  const [condition, setCondition] = useState(value.condition || 'good');
  const [laundryBeds, setLaundryBeds] = useState(value.laundryBeds || 0);
  // Only meaningful once their matching extra is actually toggled on below
  // -- same "flat regardless of quantity" fix laundry already got (see
  // laundryBeds above), now applied to Interior Windows and Finished
  // Basement too.
  const [windowCount, setWindowCount] = useState(value.windowCount || 10);
  const [basementSize, setBasementSize] = useState(value.basementSize || 'medium');

  const toggleExtra = (id) => setExtras(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  const windowsSelected = extras.includes('interior_windows');
  const basementSelected = extras.includes('basement');

  const handleNext = () => onNext({ sqftTier, bedrooms, bathrooms, cleaningType, frequency, extras, condition, laundryBeds, ...(windowsSelected ? { windowCount } : {}), ...(basementSelected ? { basementSize } : {}) });

  return (
    <StepWrapper
      title="Tell us about your home"
      subtitle="More details — more accurate estimate"
      onBack={onBack}
      onNext={handleNext}
      primaryColor={primaryColor}
      nextLabel={isFinalStep ? 'See Estimate' : undefined}
      loading={loading}
    >
      {/* Home size */}
      <StepSection label="Home size">
        <div className="calc-grid-size">
          {SQFT_OPTIONS.map(o => (
            <StepOptionCard key={o.id} selected={sqftTier === o.id} onClick={() => setSqftTier(o.id)} primaryColor={primaryColor}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{country === 'AU' ? SQFT_OPTIONS_AU_LABELS[o.id] : o.label}</div>
              <div style={{ fontSize: 12, color: COLORS.body }}>{o.sub}</div>
            </StepOptionCard>
          ))}
        </div>
      </StepSection>

      {/* Bedrooms & bathrooms */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <StepSection label="Bedrooms">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* '5' and '6+' used to be separate chips, but BEDROOM_ADDON only
                has a '5+' key -- both silently added $0 instead of $75.
                Collapsed into the one tier the backend prices. */}
            {['1','2','3','4','5+'].map(n => (
              <StepChip key={n} selected={bedrooms === n} onClick={() => setBedrooms(n)} primaryColor={primaryColor}>{n}</StepChip>
            ))}
          </div>
        </StepSection>
        <StepSection label="Bathrooms">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Top tier was '4+' here but BATHROOM_ADDON's key is '3+' --
                silently added $0 instead of $90. Matches ApartmentStep.js's
                (correct) top tier; plain '3' is still its own, separately
                priced option. */}
            {['1','1.5','2','2.5','3','3+'].map(n => (
              <StepChip key={n} selected={bathrooms === n} onClick={() => setBathrooms(n)} primaryColor={primaryColor}>{n}</StepChip>
            ))}
          </div>
        </StepSection>
      </div>

      {/* Cleaning type */}
      <StepSection label="Cleaning type">
        <div className="calc-grid-2">
          {CLEANING_TYPES.map(o => (
            <StepOptionCard key={o.id} selected={cleaningType === o.id} onClick={() => setCleaningType(o.id)} primaryColor={primaryColor}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{o.label}</div>
              <div style={{ fontSize: 12, color: COLORS.body, marginTop: 4 }}>{o.desc}</div>
            </StepOptionCard>
          ))}
        </div>
      </StepSection>

      {/* Frequency */}
      <StepSection label="How often?">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {FREQUENCY.map(f => {
            const discountPct = Math.round((frequencyDiscounts[f.id] || 0) * 100);
            return (
              <StepChip key={f.id} selected={frequency === f.id} onClick={() => setFrequency(f.id)} primaryColor={primaryColor}>
                {f.label}
                {discountPct > 0 && <span style={{ fontSize: 11, marginLeft: 4, opacity: 0.85 }}>({discountPct}% off)</span>}
              </StepChip>
            );
          })}
        </div>
      </StepSection>

      {/* Home condition */}
      <StepSection label="Home condition">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* 'Neglected' used to send id 'poor', but CONDITION_MULTIPLIERS'
              top key is 'needs_attention' -- silently applied a 1.0x (no
              increase) multiplier instead of the correct 1.22-1.28x, despite
              the line below promising 25-50% more. */}
          {[['good','Good'],['fair','Fair'],['needs_attention','Neglected']].map(([id,label]) => (
            <StepChip key={id} selected={condition === id} onClick={() => setCondition(id)} primaryColor={primaryColor}>{label}</StepChip>
          ))}
        </div>
        <p style={{ fontSize: 12, color: COLORS.muted, marginTop: 6 }}>
          Neglected homes with heavy buildup may cost 22–28% more.
        </p>
      </StepSection>

      {/* Add-ons */}
      <StepSection label="Add-ons (optional)">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {EXTRAS.map(ex => (
            <StepPillToggle key={ex.id} selected={extras.includes(ex.id)} onClick={() => toggleExtra(ex.id)} primaryColor={primaryColor}>{ex.label}</StepPillToggle>
          ))}
          <StepPillToggle selected={laundryBeds > 0} onClick={() => setLaundryBeds(laundryBeds > 0 ? 0 : 1)} primaryColor={primaryColor}>Laundry (wash, dry, remake)</StepPillToggle>
        </div>
        {laundryBeds > 0 && (
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 13, color: COLORS.body, fontWeight: 600 }}>Beds needing laundry: {laundryBeds}</label>
            <input type="range" min={1} max={8} step={1} value={laundryBeds} onChange={e => setLaundryBeds(+e.target.value)}
              style={{ width: '100%', accentColor: primaryColor, marginTop: 4 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
              <span>1 bed</span><span>8 beds</span>
            </div>
          </div>
        )}
        {windowsSelected && (
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 13, color: COLORS.body, fontWeight: 600 }}>Windows needing interior cleaning: {windowCount}</label>
            <input type="range" min={1} max={40} step={1} value={windowCount} onChange={e => setWindowCount(+e.target.value)}
              style={{ width: '100%', accentColor: primaryColor, marginTop: 4 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COLORS.muted, marginTop: 4 }}>
              <span>1 window</span><span>40+ windows</span>
            </div>
          </div>
        )}
        {basementSelected && (
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 13, color: COLORS.body, fontWeight: 600, display: 'block', marginBottom: 6 }}>Basement size</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['small','Small (< 500 sq ft)'],['medium','Medium (500–1,200 sq ft)'],['large','Large (1,200+ sq ft)']].map(([id,label]) => (
                <StepChip key={id} selected={basementSize === id} onClick={() => setBasementSize(id)} primaryColor={primaryColor}>{label}</StepChip>
              ))}
            </div>
          </div>
        )}
      </StepSection>
    </StepWrapper>
  );
}
